import { Injectable } from '@angular/core';
import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';
import { Workbook } from 'exceljs';
import { CookieService } from 'ngx-cookie-service';
import { UserDataService } from '../user-data/user-data.service';
import { decrypt } from '../../shared/crypto-js/decrypt';
import { decryptPledgeData } from 'src/app/shared/crypto-js/crypto-js';


const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
const EXCEL_EXTENSION = '.xlsx';

@Injectable({
  providedIn: 'root'
})
export class ExportService {

  clientId: any;
  clientName: any;
  clientPan: any;

  constructor(
    private cookies: CookieService,
    private userDataService: UserDataService,
  ) {
    this.getClientData();
    const encryptedUserSession = localStorage.getItem('encryptedUserSession');

    if (encryptedUserSession) {
      const decryptedStr = decryptPledgeData(encryptedUserSession);
      const decryptedData = JSON.parse(decryptedStr);
      this.clientId = decryptedData.accountId;
    }
  }

  getClientData() {
    const userDataEncrypted = localStorage.getItem('userData');
    const userDataTime = Number(localStorage.getItem('userDataTime') || '0');
    const now = Date.now();

    const isDataExpired = now - userDataTime > 30 * 60 * 1000;

    if (!userDataEncrypted || isDataExpired) {
      this.userDataService.getUserData().subscribe((userData) => {
        if (userData) {
          this.clientName = userData.name;
          // this.clientPan = userData.pan;
            this.clientPan = this.maskPan(userData.pan);
        }
      });
      return;
    }
    

    const decrypted = decrypt(userDataEncrypted);
    const userData = JSON.parse(decrypted);
    this.clientName = userData.name;
    this.clientPan = this.maskPan(userData.pan);
  }

  maskPan(pan: string): string {
  if (!pan) return '';

  return pan.replace(/^.{5}/, 'XXXX');
}


  public exportAsExcelFile(json: any[], excelFileName: string): void {
    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(json);
    const workbook: XLSX.WorkBook = { Sheets: { 'data': worksheet }, SheetNames: ['data'] };
    const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    this.saveAsExcelFile(excelBuffer, excelFileName);
  }

  public exportHoldingMultipleAsExcelFile(json: any[], sheetName: any[], excelFileName: string): void {
    let wb = XLSX.utils.book_new();
    for (var i = 0; i < sheetName.length; i++) {
      const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(json[i]);
      XLSX.utils.book_append_sheet(wb, worksheet, sheetName[i]);
    }
    const excelBuffer: any = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    this.saveAsExcelFile(excelBuffer, excelFileName);
  }

  public exportMultipleAsExcelFile(json: any[], sheetName: any[], dateRange?: any[], excelFileName?: string): void {
    let wb = XLSX.utils.book_new();
    for (var i = 0; i < sheetName.length; i++) {
      const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet([]);
      XLSX.utils.sheet_add_aoa(worksheet, [[dateRange[i]]], { origin: "A2" });
      XLSX.utils.sheet_add_json(worksheet, json[i], { origin: "A4" });
      XLSX.utils.book_append_sheet(wb, worksheet, sheetName[i]);
    }
    const excelBuffer: any = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    this.saveAsExcelFile(excelBuffer, excelFileName);
  }

  public exportTestExcelFile(json: any[], sheetName: any[], excelFileName: string): void {
    let wb = XLSX.utils.book_new();
    for (var i = 0; i < sheetName.length; i++) {
      const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet([]);
      XLSX.utils.sheet_add_aoa(ws, [["test"]], { origin: "A1" })
      XLSX.utils.sheet_add_json(ws, json[i], { origin: "A2" })
      XLSX.utils.book_append_sheet(wb, ws, sheetName[i]);
    }
    const excelBuffer: any = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    this.saveAsExcelFile(excelBuffer, excelFileName);
  }

  public exportExcelFileData(json: any[], headers: any[], sheetName: any[], dateRange?: any[], excelFileName?: string) {
    const workbook = new Workbook();
    let image64BaseData: any;
    this.getBase64ImageFromUrl().then((result) => { image64BaseData = result }).catch((err) => { console.error(err) })

    setTimeout(() => {
      for (var i = 0; i < sheetName.length; i++) {
        const worksheet = workbook.addWorksheet(sheetName[i] || 'AliceBlue');

        let aliceBlueLogo = workbook.addImage({
          base64: image64BaseData,
          extension: 'png',
        });
        worksheet.mergeCells('A1:C4');
        worksheet.addImage(aliceBlueLogo, 'A2:C4');
        worksheet.addRow([]);

        const titleRow = worksheet.addRow(dateRange[i]);
        titleRow.font = {
          name: 'Calibri',
          family: 4,
          size: 16,
          bold: true
        };
        titleRow.alignment = { horizontal: 'start' };
        worksheet.mergeCells('A6:M6');
        // worksheet.mergeCells('A1:M1');
        worksheet.addRow([]);

        const headerRow = worksheet.addRow(headers[i]);
        headerRow.font = {
          bold: true
        };
        headerRow.eachCell((cell) => {
          // cell.fill = {
          //   type: 'pattern',
          //   pattern: 'solid',
          //   fgColor: { argb: 'FFFFFF00' },
          //   bgColor: { argb: 'FF0000FF' }
          // };
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
        });

        json[i].forEach((d: any) => {
          let x2 = Object.keys(d);
          let temp = []
          for (let y of x2) {
            temp.push(d[y])
          }
          worksheet.addRow(temp);
        });
      }

      workbook.xlsx.writeBuffer().then((data: any) => {
        const excelBuffer = new Blob([data], {
          type: EXCEL_TYPE
        });
        this.saveAsExcelFile(excelBuffer, excelFileName);
      });
    }, 500)
  }

  // public exportTaxPnlData(json: any[], headers: any[], sheetName: any[], dateRange?: any[], excelFileName?: string) {
  //   const workbook = new Workbook();
  //   let image64BaseData: any;
  //   this.getBase64ImageFromUrl().then((result) => { image64BaseData = result }).catch((err) => { console.error(err) })

  //   setTimeout(() => {
  //     for (var i = 0; i < sheetName.length; i++) {
  //       const worksheet = workbook.addWorksheet(sheetName[i] || 'Aliceblue', { views: [{ showGridLines: false }] });

  //       let aliceBlueLogo = workbook.addImage({
  //         base64: image64BaseData,
  //         extension: 'png',
  //       });
  //       worksheet.addImage(aliceBlueLogo, { tl: { col: 0.2, row: 0.5 }, ext: { width: 150, height: 70 } });
  //       worksheet.addRow([]);
  //       worksheet.addRow([]);
  //       worksheet.addRow([]);
  //       worksheet.addRow([]);
  //       worksheet.addRow([]);
  //       worksheet.addRow([]);

  //       const clientId = worksheet.addRow(['Client ID', this.clientId]);
  //       clientId.font = { bold: true }
  //       const clientName = worksheet.addRow(['Client Name', this.clientName]);
  //       clientName.font = { bold: true }
  //       const pan = worksheet.addRow(['Pan', this.clientPan]);
  //       pan.font = { bold: true }

  //       worksheet.addRow([]);

  //       if (sheetName[i] == 'Tradewise') {
  //         worksheet.columns = [
  //           { width: 25 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 20 },
  //           { width: 20 },
  //           { width: 20 },
  //           { width: 15 },
  //           { width: 15 },
  //         ];
  //         const titleRow = worksheet.addRow(dateRange[i]);
  //         titleRow.font = {
  //           name: 'Calibri',
  //           family: 4,
  //           bold: true
  //         };
  //         titleRow.alignment = { horizontal: 'start' };

  //         let headerArray = headers[i];
  //         let jsonArrayData = json[i];
  //         let segmentArray = ['Equity', 'Mutual Fund', 'FNO', 'Currency', 'Commodity','NSE Commodity'];

  //         for (let i = 0; i < headerArray.length; i++) {
  //           worksheet.addRow([]);
  //           const segmentTradewise = worksheet.addRow([segmentArray[i]])
  //           segmentTradewise.font = {
  //             bold: true
  //           };
  //           worksheet.addRow([]);
  //           const headerRow = worksheet.addRow(headerArray[i]);
  //           headerRow.font = {
  //             bold: true
  //           };
  //           jsonArrayData[i].forEach((d: any) => {
  //             let x2 = Object.keys(d);
  //             let temp = [];
  //             for (let y of x2) {
  //               temp.push(d[y])
  //             }
  //             const alignmentcol = worksheet.addRow(temp);
  //             alignmentcol.alignment = { horizontal: 'left' };
  //           });
  //           worksheet.addRow([]);
  //         }
  //       }
  //       else if (sheetName[i] == 'Equity') {
  //         worksheet.columns = [
  //           { width: 40 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 26 },
  //         ];
  //         const titleRow = worksheet.addRow(dateRange[i]);
  //         titleRow.font = {
  //           name: 'Calibri',
  //           family: 4,
  //           bold: true
  //         };
  //         titleRow.alignment = { horizontal: 'start' };

  //         let headerArray = headers[i];
  //         let jsonArrayData = json[i];
  //         // let equityArray = ['Realized Profit Breakdown', 'Turnover Breakdown', 'Charges', 'Other Charges', 'Intraday', 'Short Term Trades', 'Long Term Trades'];
  //         let equityArray = ['Realized Profit Breakdown', 'Charges', 'Other Charges', 'Intraday', 'Short Term Trades', 'Long Term Trades'];

  //         for (let i = 0; i < headerArray.length; i++) {
  //           worksheet.addRow([]);
  //           const segmentEquity = worksheet.addRow([equityArray[i]]);
  //           segmentEquity.font = {
  //             bold: true
  //           };
  //           worksheet.addRow([]);
  //           const headerRow = worksheet.addRow(headerArray[i]);
  //           headerRow.font = {
  //             bold: true
  //           };
  //           jsonArrayData[i].forEach((d: any) => {
  //             let x2 = Object.keys(d);
  //             let temp = []
  //             for (let y of x2) {
  //               temp.push(d[y])
  //             }
  //             const alignmentText = worksheet.addRow(temp);
  //             alignmentText.alignment = { horizontal: 'left' };
  //           });
  //           worksheet.addRow([]);
  //         }
  //       }
  //       else if (sheetName[i] == 'Mutual Fund') {
  //         worksheet.columns = [
  //           { width: 25 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //         ];
  //         const titleRow = worksheet.addRow(dateRange[i]);
  //         titleRow.font = {
  //           name: 'Calibri',
  //           family: 4,
  //           bold: true
  //         };
  //         titleRow.alignment = { horizontal: 'start' };

  //         let headerArray = headers[i];
  //         let jsonArrayData = json[i];
  //         // let mutualFundArray = ['Realized Profit Breakdown', 'Charges', 'Other Charges', 'Short Term Trades Equity', 'Long Term Trades Equity', 'Short Term Trades Debt', 'Long Term Trades Debt'];
  //         let mutualFundArray = ['Realized Profit Breakdown', 'Short Term Trades Equity', 'Long Term Trades Equity', 'Short Term Trades Debt', 'Long Term Trades Debt', 'Short Term Trades Others', 'Long Term Trades Others'];

  //         for (let i = 0; i < headerArray.length; i++) {
  //           worksheet.addRow([]);
  //           const segmentMutualFund = worksheet.addRow([mutualFundArray[i]]);
  //           segmentMutualFund.font = {
  //             bold: true
  //           };
  //           worksheet.addRow([]);
  //           const headerRow = worksheet.addRow(headerArray[i]);
  //           headerRow.font = {
  //             bold: true
  //           };
  //           jsonArrayData[i].forEach((d: any) => {
  //             let x2 = Object.keys(d);
  //             let temp = [];
  //             for (let y of x2) {
  //               temp.push(d[y])
  //             }
  //             const alignmentText = worksheet.addRow(temp);
  //             alignmentText.alignment = { horizontal: 'left' };
  //           });
  //           worksheet.addRow([]);
  //         }
  //       }
  //       else if (sheetName[i] == 'FNO') {
  //         worksheet.columns = [
  //           { width: 25 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 26 },
  //         ];
  //         const titleRow = worksheet.addRow(dateRange[i]);
  //         titleRow.font = {
  //           name: 'Calibri',
  //           family: 4,
  //           bold: true
  //         };
  //         titleRow.alignment = { horizontal: 'start' };

  //         let headerArray = headers[i];
  //         let jsonArrayData = json[i];
  //         // let fnoArray = ['Realized Profit Breakdown', 'Turnover Breakdown', 'Charges', 'Other Charges', 'Options', 'Futures'];
  //         let fnoArray = ['Realized Profit Breakdown', 'Charges', 'Other Charges', 'Options', 'Futures'];

  //         for (let i = 0; i < headerArray.length; i++) {
  //           worksheet.addRow([]);
  //           const segmentFNO = worksheet.addRow([fnoArray[i]]);
  //           segmentFNO.font = {
  //             bold: true
  //           };
  //           worksheet.addRow([]);
  //           const headerRow = worksheet.addRow(headerArray[i]);
  //           headerRow.font = {
  //             bold: true
  //           };
  //           jsonArrayData[i].forEach((d: any) => {
  //             let x2 = Object.keys(d);
  //             let temp = [];
  //             for (let y of x2) {
  //               temp.push(d[y])
  //             }
  //             const alignmentText = worksheet.addRow(temp);
  //             alignmentText.alignment = { horizontal: 'left' };
  //           });
  //           worksheet.addRow([]);
  //         }
  //       }
  //       else if (sheetName[i] == 'Currency') {
  //         worksheet.columns = [
  //           { width: 25 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 26 },
  //         ];
  //         const titleRow = worksheet.addRow(dateRange[i]);
  //         titleRow.font = {
  //           name: 'Calibri',
  //           family: 4,
  //           bold: true
  //         };
  //         titleRow.alignment = { horizontal: 'start' };

  //         let headerArray = headers[i];
  //         let jsonArrayData = json[i];
  //         // let currencyArray = ['Realized Profit Breakdown', 'Turnover Breakdown', 'Charges', 'Other Charges', 'Options', 'Futures'];
  //         let currencyArray = ['Realized Profit Breakdown', 'Charges', 'Other Charges', 'Options', 'Futures'];

  //         for (let i = 0; i < headerArray.length; i++) {
  //           worksheet.addRow([]);
  //           const segmentCurrency = worksheet.addRow([currencyArray[i]]);
  //           segmentCurrency.font = {
  //             bold: true
  //           };
  //           worksheet.addRow([]);
  //           const headerRow = worksheet.addRow(headerArray[i]);
  //           headerRow.font = {
  //             bold: true
  //           };
  //           jsonArrayData[i].forEach((d: any) => {
  //             let x2 = Object.keys(d);
  //             let temp = [];
  //             for (let y of x2) {
  //               temp.push(d[y])
  //             }
  //             const alignmentText = worksheet.addRow(temp);
  //             alignmentText.alignment = { horizontal: 'left' };
  //           });
  //           worksheet.addRow([]);
  //         }
  //       }
  //       else if (sheetName[i] == 'Commodity') {
  //         worksheet.columns = [
  //           { width: 25 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 26 },
  //         ];
  //         const titleRow = worksheet.addRow(dateRange[i]);
  //         titleRow.font = {
  //           name: 'Calibri',
  //           family: 4,
  //           bold: true
  //         };
  //         titleRow.alignment = { horizontal: 'start' };

  //         let headerArray = headers[i];
  //         let jsonArrayData = json[i];
  //         // let commodityArray = ['Realized Profit Breakdown', 'Turnover Breakdown', 'Charges', 'Other Charges', 'Options', 'Futures'];
  //         let commodityArray = ['Realized Profit Breakdown', 'Charges', 'Other Charges', 'Options', 'Futures'];

  //         for (let i = 0; i < headerArray.length; i++) {
  //           worksheet.addRow([]);
  //           const segmentCurrency = worksheet.addRow([commodityArray[i]]);
  //           segmentCurrency.font = {
  //             bold: true
  //           };
  //           worksheet.addRow([]);
  //           const headerRow = worksheet.addRow(headerArray[i]);
  //           headerRow.font = {
  //             bold: true
  //           };
  //           jsonArrayData[i].forEach((d: any) => {
  //             let x2 = Object.keys(d);
  //             let temp = [];
  //             for (let y of x2) {
  //               temp.push(d[y])
  //             }
  //             const alignmentText = worksheet.addRow(temp);
  //             alignmentText.alignment = { horizontal: 'left' };
  //           });
  //           worksheet.addRow([]);
  //         }
  //       }
  //       else if (sheetName[i] == 'NSE Commodity') {
  //         worksheet.columns = [
  //           { width: 25 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 26 },
  //         ];
  //         const titleRow = worksheet.addRow(dateRange[i]);
  //         titleRow.font = {
  //           name: 'Calibri',
  //           family: 4,
  //           bold: true
  //         };
  //         titleRow.alignment = { horizontal: 'start' };

  //         let headerArray = headers[i];
  //         let jsonArrayData = json[i];
  //         // let commodityArray = ['Realized Profit Breakdown', 'Turnover Breakdown', 'Charges', 'Other Charges', 'Options', 'Futures'];
  //         let NsecomArray = ['Realized Profit Breakdown', 'Charges', 'Other Charges', 'Options', 'Futures'];

  //         for (let i = 0; i < headerArray.length; i++) {
  //           worksheet.addRow([]);
  //           const segmentCurrency = worksheet.addRow([NsecomArray[i]]);
  //           segmentCurrency.font = {
  //             bold: true
  //           };
  //           worksheet.addRow([]);
  //           const headerRow = worksheet.addRow(headerArray[i]);
  //           headerRow.font = {
  //             bold: true
  //           };
  //           jsonArrayData[i].forEach((d: any) => {
  //             let x2 = Object.keys(d);
  //             let temp = [];
  //             for (let y of x2) {
  //               temp.push(d[y])
  //             }
  //             const alignmentText = worksheet.addRow(temp);
  //             alignmentText.alignment = { horizontal: 'left' };
  //           });
  //           worksheet.addRow([]);
  //         }
  //       }
  //       else if (sheetName[i] == 'Other Credits and Debits') {
  //         worksheet.columns = [
  //           { width: 80 },
  //           { width: 20 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //         ];
  //         const titleRow = worksheet.addRow(dateRange[i]);
  //         titleRow.font = {
  //           name: 'Calibri',
  //           family: 4,
  //           bold: true
  //         };
  //         titleRow.alignment = { horizontal: 'start' };

  //         let headerArray = headers[i];
  //         let jsonArrayData = json[i];
  //         // let otherDebitCreditArray = ['Equity', 'Mutual Funds', 'F&O', 'Currency', 'Commodity'];

  //         for (let i = 0; i < headerArray.length; i++) {
  //           // worksheet.addRow([]);
  //           // const segmentOtherDebitCredit = worksheet.addRow([otherDebitCreditArray[i]]);
  //           // segmentOtherDebitCredit.font = {
  //           //   bold: true
  //           // };
  //           worksheet.addRow([]);
  //           const headerRow = worksheet.addRow(headerArray[i]);
  //           headerRow.font = {
  //             bold: true
  //           };
  //           jsonArrayData[i].forEach((d: any) => {
  //             let x2 = Object.keys(d);
  //             let temp = [];
  //             for (let y of x2) {
  //               temp.push(d[y])
  //             }
  //             const alignmentText = worksheet.addRow(temp);
  //             alignmentText.alignment = { horizontal: 'left' };
  //           });
  //           worksheet.addRow([]);
  //         }
  //       }
  //       else if (sheetName[i] == 'Open Positions') {
  //         worksheet.columns = [
  //           { width: 45 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 18 },
  //           { width: 18 },
  //           { width: 15 },
  //         ];
  //         const titleRow = worksheet.addRow(dateRange[i]);
  //         titleRow.font = {
  //           name: 'Calibri',
  //           family: 4,
  //           bold: true
  //         };
  //         titleRow.alignment = { horizontal: 'start' };

  //         let headerArray = headers[i];
  //         let jsonArrayData = json[i];
  //         let openPositionArray = ['Open Positions for Equity', 'Open Positions for Mutual Fund', 'Open Positions for F&O', 'Open Positions for Currency', 'Open Positions for Commodity'];

  //         for (let i = 0; i < headerArray.length; i++) {
  //           worksheet.addRow([]);
  //           const segmentOtherDebitCredit = worksheet.addRow([openPositionArray[i]]);
  //           segmentOtherDebitCredit.font = {
  //             bold: true
  //           };
  //           worksheet.addRow([]);
  //           const headerRow = worksheet.addRow(headerArray[i]);
  //           headerRow.font = {
  //             bold: true
  //           };
  //           jsonArrayData[i].forEach((d: any) => {
  //             let x2 = Object.keys(d);
  //             let temp = [];
  //             for (let y of x2) {
  //               temp.push(d[y])
  //             }
  //             const alignmentText = worksheet.addRow(temp);
  //             alignmentText.alignment = { horizontal: 'left' };
  //           });
  //           worksheet.addRow([]);
  //         }
  //       }
  //       else if (sheetName[i] == 'Equity Dividends') {
  //         worksheet.columns = [
  //           { width: 25 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 15 },
  //           { width: 20 },
  //           { width: 15 },
  //         ];
  //         const titleRow = worksheet.addRow(dateRange[i]);
  //         titleRow.font = {
  //           name: 'Calibri',
  //           family: 4,
  //           bold: true
  //         };
  //         titleRow.alignment = { horizontal: 'start' };

  //         worksheet.addRow([]);

  //         let headerArray = headers[i];
  //         let jsonArrayData = json[i];

  //         for (let i = 0; i < headerArray.length; i++) {
  //           worksheet.addRow([]);
  //           const headerRow = worksheet.addRow(headerArray[i]);
  //           headerRow.font = {
  //             bold: true
  //           };
  //           jsonArrayData[i].forEach((d: any) => {
  //             let x2 = Object.keys(d);
  //             let temp = [];
  //             for (let y of x2) {
  //               temp.push(d[y])
  //             }
  //             const alignmentText = worksheet.addRow(temp);
  //             alignmentText.alignment = { horizontal: 'left' };
  //           });
  //           worksheet.addRow([]);
  //           worksheet.addRow([]);
  //         }

  //         const equityDividendText = worksheet.addRow(['Dividends are credited directly to your registered bank account']);
  //         equityDividendText.font = { bold: true }
  //       }
  //       else if (sheetName[i] == 'Ledger Balances') {
  //         worksheet.columns = [
  //           { width: 25 },
  //           { width: 15 },
  //           { width: 15 },
  //         ];
  //         const titleRow = worksheet.addRow(dateRange[i]);
  //         titleRow.font = {
  //           name: 'Calibri',
  //           family: 4,
  //           bold: true
  //         };
  //         titleRow.alignment = { horizontal: 'start' };

  //         let headerArray = headers[i];
  //         let jsonArrayData = json[i];
  //         // let ledgerBalanceArray = ['Alice Blue', 'Alice Blue Commodities Pvt Ltd'];
  //         let ledgerBalanceArray = ['Alice Blue'];

  //         for (let i = 0; i < headerArray.length; i++) {
  //           worksheet.addRow([]);
  //           const segmentLeadgerBalance = worksheet.addRow([ledgerBalanceArray[i]]);
  //           segmentLeadgerBalance.font = {
  //             bold: true
  //           };
  //           const headerRow = worksheet.addRow(headerArray[i]);
  //           headerRow.font = {
  //             bold: true
  //           };
  //           jsonArrayData[i].forEach((d: any) => {
  //             let x2 = Object.keys(d);
  //             let temp = [];
  //             for (let y of x2) {
  //               temp.push(d[y])
  //             }
  //             const alignmentText = worksheet.addRow(temp);
  //             alignmentText.alignment = { horizontal: 'left' };
  //           });
  //           worksheet.addRow([]);
  //         }
  //       }
  //     }

  //     workbook.xlsx.writeBuffer().then((data: any) => {
  //       const excelBuffer = new Blob([data], {
  //         type: EXCEL_TYPE
  //       });
  //       this.saveAsExcelFile(excelBuffer, excelFileName);
  //     });
  //   }, 1500)
  // }

  public async exportTaxPnlData(
  json: any[],
  headers: any[],
  sheetName: any[],
  dateRange?: any[],
  excelFileName?: string
) {
  const workbook = new Workbook();

  // ✅ Load image safely
  let image64BaseData: any;
  try {
    image64BaseData = await this.getBase64ImageFromUrl();
  } catch (err) {
    console.error(err);
  }

  for (let i = 0; i < (sheetName?.length || 0); i++) {

    const worksheet = workbook.addWorksheet(sheetName[i] || 'Aliceblue', {
      views: [{ showGridLines: false }]
    });

    // ✅ Add logo only if available
    if (image64BaseData) {
      const logo = workbook.addImage({
        base64: image64BaseData,
        extension: 'png',
      });
      worksheet.addImage(logo, { tl: { col: 0.2, row: 0.5 }, ext: { width: 150, height: 70 } });
    }

    // spacing
    for (let j = 0; j < 6; j++) worksheet.addRow([]);

    const clientId = worksheet.addRow(['Client ID', this.clientId]);
    clientId.font = { bold: true };

    const clientName = worksheet.addRow(['Client Name', this.clientName]);
    clientName.font = { bold: true };

    const pan = worksheet.addRow(['Pan', this.clientPan]);
    pan.font = { bold: true };

    worksheet.addRow([]);

    const headerArray = headers?.[i] ?? [];
    const jsonArrayData = json?.[i] ?? [];
    const range = dateRange?.[i] ?? [];

    const titleRow = worksheet.addRow(range);
    titleRow.font = { bold: true };
    titleRow.alignment = { horizontal: 'start' };

    // helper to safely render rows
    const renderRows = (dataList: any[]) => {
      (dataList ?? []).forEach((d: any) => {
        const values = Object.values(d || {});
        const row = worksheet.addRow(values);
        row.alignment = { horizontal: 'left' };
      });
    };

    // ───────── SWITCH BASED ON SHEET ─────────
    switch (sheetName[i]) {

      case 'Tradewise':
        worksheet.columns = new Array(13).fill({ width: 15 });

        const segmentArray = ['Equity', 'Mutual Fund', 'FNO', 'Currency', 'Commodity', 'NSE Commodity'];

        headerArray.forEach((header: any, idx: number) => {
          worksheet.addRow([]);
          worksheet.addRow([segmentArray[idx]]).font = { bold: true };
          worksheet.addRow([]);

          worksheet.addRow(header).font = { bold: true };

          renderRows(jsonArrayData?.[idx]);
          worksheet.addRow([]);
        });
        break;

      case 'Equity':
        worksheet.columns = new Array(13).fill({ width: 15 });

        const equityArray = ['Realized Profit Breakdown', 'Charges', 'Other Charges', 'Intraday', 'Short Term Trades', 'Long Term Trades'];

        headerArray.forEach((header: any, idx: number) => {
          worksheet.addRow([]);
          worksheet.addRow([equityArray[idx]]).font = { bold: true };
          worksheet.addRow([]);

          worksheet.addRow(header).font = { bold: true };

          renderRows(jsonArrayData?.[idx]);
          worksheet.addRow([]);
        });
        break;

      case 'Mutual Fund':
        worksheet.columns = new Array(6).fill({ width: 15 });

        const mfArray = ['Realized Profit Breakdown', 'Short Term Trades Equity', 'Long Term Trades Equity', 'Short Term Trades Debt', 'Long Term Trades Debt', 'Short Term Trades Others', 'Long Term Trades Others'];

        headerArray.forEach((header: any, idx: number) => {
          worksheet.addRow([]);
          worksheet.addRow([mfArray[idx]]).font = { bold: true };
          worksheet.addRow([]);

          worksheet.addRow(header).font = { bold: true };

          renderRows(jsonArrayData?.[idx]);
          worksheet.addRow([]);
        });
        break;

      case 'FNO':
      case 'Currency':
      case 'Commodity':
      case 'NSE Commodity':

        worksheet.columns = new Array(13).fill({ width: 15 });

        const commonArray = ['Realized Profit Breakdown', 'Charges', 'Other Charges', 'Options', 'Futures'];

        headerArray.forEach((header: any, idx: number) => {
          worksheet.addRow([]);
          worksheet.addRow([commonArray[idx]]).font = { bold: true };
          worksheet.addRow([]);

          worksheet.addRow(header).font = { bold: true };

          renderRows(jsonArrayData?.[idx]);
          worksheet.addRow([]);
        });
        break;

      case 'Other Credits and Debits':
        worksheet.columns = new Array(6).fill({ width: 15 });

        headerArray.forEach((header: any, idx: number) => {
          worksheet.addRow([]);
          worksheet.addRow(header).font = { bold: true };

          renderRows(jsonArrayData?.[idx]);
          worksheet.addRow([]);
        });
        break;

      case 'Open Positions':
        worksheet.columns = new Array(9).fill({ width: 15 });

        const openArray = [
          'Open Positions for Equity',
          'Open Positions for Mutual Fund',
          'Open Positions for F&O',
          'Open Positions for Currency',
          'Open Positions for Commodity',
          'Open Positions for NSE Commodity'
        ];

        headerArray.forEach((header: any, idx: number) => {
          worksheet.addRow([]);
          worksheet.addRow([openArray[idx]]).font = { bold: true };
          worksheet.addRow([]);

          worksheet.addRow(header).font = { bold: true };

          renderRows(jsonArrayData?.[idx]);
          worksheet.addRow([]);
        });
        break;

      case 'Equity Dividends':
        worksheet.columns = new Array(6).fill({ width: 15 });

        headerArray.forEach((header: any, idx: number) => {
          worksheet.addRow([]);
          worksheet.addRow(header).font = { bold: true };

          renderRows(jsonArrayData?.[idx]);
          worksheet.addRow([]);
        });

        worksheet.addRow([]);
        worksheet.addRow(['Dividends are credited directly to your registered bank account']).font = { bold: true };
        break;

      case 'Ledger Balances':
        worksheet.columns = new Array(3).fill({ width: 15 });

        const ledgerArray = ['Alice Blue'];

        headerArray.forEach((header: any, idx: number) => {
          worksheet.addRow([]);
          worksheet.addRow([ledgerArray[idx]]).font = { bold: true };

          worksheet.addRow(header).font = { bold: true };

          renderRows(jsonArrayData?.[idx]);
          worksheet.addRow([]);
        });
        break;
    }
  }

  // ───── Export ─────
  const buffer = await workbook.xlsx.writeBuffer();

  const blob = new Blob([buffer], { type: EXCEL_TYPE });
  this.saveAsExcelFile(blob, excelFileName);
}

  private saveAsExcelFile(buffer: any, fileName: string): void {
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE
    });
    var today = new Date();
    var formattedDate = today.getDate() + '_' + (today.getMonth() + 1) + '_' + today.getFullYear();
    var fileNameWithDate = fileName + '_export_' + formattedDate + EXCEL_EXTENSION;
    FileSaver.saveAs(data, fileNameWithDate);
    // FileSaver.saveAs(data, fileName + '_export_' + new Date().toLocaleDateString() + EXCEL_EXTENSION);
  }



  public exportExcelFileDatas(json: any[], headers: any[], sheetName: any[], dateRange?: any[], excelFileName?: string) {
    const workbook = new Workbook();
    let image64BaseData: any;
    this.getBase64ImageFromUrl().then((result) => { image64BaseData = result }).catch((err) => { console.error(err) })

    setTimeout(() => {
      const sheetCount = 7; // Total number of sheets

      // Ensure that all arrays have enough data for the desired number of sheets
      const minLen = Math.min(sheetCount, sheetName.length, headers.length, json.length);

      for (var i = 0; i < minLen; i++) { // Iterate up to minLen
        const worksheet = workbook.addWorksheet(sheetName[i] || 'ALiceblue');

        let aliceBlueLogo = workbook.addImage({
          base64: image64BaseData,
          extension: 'png',
        });
        worksheet.mergeCells('A1:C4');
        worksheet.addImage(aliceBlueLogo, 'A2:C4');
        worksheet.addRow([]);

        const titleRow = worksheet.addRow(dateRange[i]);
        titleRow.font = {
          name: 'Calibri',
          family: 4,
          size: 16,
          bold: true
        };
        titleRow.alignment = { horizontal: 'start' };
        worksheet.mergeCells('A6:M6');
        // worksheet.mergeCells('A1:M1');
        worksheet.addRow([]);

        const headerRow = worksheet.addRow(headers[i]);
        headerRow.font = {
          bold: true
        };
        headerRow.eachCell((cell) => {
          // cell.fill = {
          //   type: 'pattern',
          //   pattern: 'solid',
          //   fgColor: { argb: 'FFFFFF00' },
          //   bgColor: { argb: 'FF0000FF' }
          // };
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
        });

        json[i].forEach((d: any) => {
          let x2 = Object.keys(d);
          let temp = []
          for (let y of x2) {
            temp.push(d[y])
          }
          worksheet.addRow(temp);
        });
      }

      workbook.xlsx.writeBuffer().then((data: any) => {
        const excelBuffer = new Blob([data], {
          type: EXCEL_TYPE
        });
        this.saveAsExcelFile(excelBuffer, excelFileName);
      });
    }, 500)
  }

  async getBase64ImageFromUrl() {
    let imageUrl = "/assets/images/alice.png";
    var res = await fetch(imageUrl);
    var blob = await res.blob();

    return new Promise((resolve, reject) => {
      var reader = new FileReader();
      reader.onload = () => {
        resolve(reader.result);
      };

      reader.onerror = () => {
        return reject(this);
      };
      reader.readAsDataURL(blob);
    })
  }
}